import { useEffect, useState, useMemo } from "react";
import { Star, Search as SearchIcon, Loader2, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/common";
import { useAuth } from "../context/AuthContext";

/* Admin toggle for the Home "Featured Destinations" shelf.
   Platform moderators can promote/demote destinations without hitting the DB. */

export default function AdminDestinations() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all"); // all | featured | not
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/destinations", { params: { limit: 100 } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load destinations");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (d) => {
    setBusyId(d.id);
    try {
      const next = !d.is_featured;
      await api.patch(`/destinations/${d.id}/featured`, { is_featured: next });
      setItems((cur) => cur.map((x) => x.id === d.id ? { ...x, is_featured: next } : x));
      toast.success(next ? `Promoted ${d.name}` : `Demoted ${d.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    if (!items) return [];
    const term = q.trim().toLowerCase();
    return items.filter((d) => {
      if (only === "featured" && !d.is_featured) return false;
      if (only === "not" && d.is_featured) return false;
      if (!term) return true;
      return (d.name || "").toLowerCase().includes(term)
        || (d.country || "").toLowerCase().includes(term)
        || (d.region || "").toLowerCase().includes(term);
    });
  }, [items, q, only]);

  if (user && !["moderator", "admin"].includes(user.role)) {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        <p className="font-serif font-800 text-2xl text-primary">Moderators only</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-destinations-page">
      <PageHeader
        eyebrow="Admin · Curator shelf"
        title="Featured destinations"
        subtitle="Promote or demote destinations on the Home Featured strip. Changes are live within seconds." />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8 space-y-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex-1 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-destinations-search"
              placeholder="Search by name, country, region…"
              className="w-full border border-border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div className="inline-flex items-center gap-1 border border-border rounded-lg p-0.5" data-testid="admin-destinations-filter">
            {[
              ["all", "All"],
              ["featured", "Featured"],
              ["not", "Not featured"],
            ].map(([k, label]) => (
              <button key={k} onClick={() => setOnly(k)}
                data-testid={`admin-destinations-filter-${k}`}
                className={`px-3 py-1.5 rounded-md text-xs font-700 transition-colors ${
                  only === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"
                }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {items === null ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground text-sm" data-testid="admin-destinations-empty">
            No destinations match your filters.
          </div>
        ) : (
          <ul className="divide-y divide-border border border-border rounded-2xl bg-card overflow-hidden" data-testid="admin-destinations-list">
            {filtered.map((d) => (
              <li key={d.id} className="flex items-center gap-3 p-3 sm:p-4" data-testid={`admin-destinations-row-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-serif font-700 text-base text-primary truncate flex items-center gap-2">
                    {d.name}
                    {d.is_featured && (
                      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-800 uppercase tracking-widest px-1.5 py-0.5 rounded-full">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{d.country || "—"} · {d.region || "—"}</div>
                </div>
                <button onClick={() => toggle(d)} disabled={busyId === d.id}
                  data-testid={`admin-destinations-toggle-${d.id}`}
                  className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-700 min-h-[36px] disabled:opacity-60 ${
                    d.is_featured
                      ? "border border-amber-500 bg-white text-amber-800 hover:bg-amber-50"
                      : "bg-primary text-primary-foreground hover:opacity-95"
                  }`}>
                  {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
                  {d.is_featured ? "Demote" : "Promote"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
